import { z } from 'zod';

export const READINESS = ['bereit', 'in_arbeit', 'nicht_im_scope'] as const;
export type Readiness = (typeof READINESS)[number];

export const POLARITY = ['positiv', 'negativ'] as const;
export type Polarity = (typeof POLARITY)[number];

export const GLOSSARY_SOURCE = ['sap_standard', 'kundenspezifisch'] as const;
export type GlossarySource = (typeof GLOSSARY_SOURCE)[number];

export const ModuleCreateSchema = z.object({
  name_de: z.string().min(1),
  name_en: z.string().default(''),
});

export const ProcessCreateSchema = z.object({
  module_id: z.string().uuid().nullable().default(null),
  name_de: z.string().min(1),
  name_en: z.string().default(''),
  sap_scope_item_id: z.string().nullable().default(null),
  in_scope: z.boolean().default(true),
  notes: z.string().nullable().default(null),
  // #112: EIN Teilprojekt je Prozess und Projekt — vorher ein Array (`node_ids`), das faktisch nie
  // mehr als einen Wert trug (die Maske las immer nur node_ids[0]). Als Array blieb die Regel eine
  // Konvention; als einzelner Wert steht sie im Typ. `null` = „Ohne Teilprojekt".
  // Ein Stream ist hier kein zulässiges Ziel — das prüft der Server (services/structure/labels.ts).
  node_id: z.string().uuid().nullable().default(null),
});
export const ProcessUpdateSchema = ProcessCreateSchema.partial();

export const TestCaseCreateSchema = z.object({
  process_id: z.string().uuid().nullable().default(null),
  title_de: z.string().min(1),
  title_en: z.string().default(''),
  precondition_de: z.string().nullable().default(null),
  precondition_en: z.string().nullable().default(null),
  expected_de: z.string().nullable().default(null),
  expected_en: z.string().nullable().default(null),
  polarity: z.enum(POLARITY).nullable().default(null),
  readiness: z.enum(READINESS).default('in_arbeit'),
  default_tester_id: z.string().uuid().nullable().default(null),
});
export const TestCaseUpdateSchema = TestCaseCreateSchema.partial();

export const StepCreateSchema = z.object({
  description_de: z.string().min(1),
  description_en: z.string().default(''),
  tcode: z.string().nullable().default(null),
  expected_output_de: z.string().nullable().default(null),
  expected_output_en: z.string().nullable().default(null),
  // #66: Schritt-Input ("Input Data") — symmetrisch zu expected_output_*, bidirektional befuellt.
  input_de: z.string().nullable().default(null),
  input_en: z.string().nullable().default(null),
  notes: z.string().nullable().default(null),
});
export const StepUpdateSchema = StepCreateSchema.partial();
export const StepReorderSchema = z.object({ step_ids: z.array(z.string().uuid()).min(1) });

export const E2eCreateSchema = z.object({
  name_de: z.string().min(1),
  name_en: z.string().default(''),
});
export const E2eUpdateSchema = E2eCreateSchema.partial();
export const E2eMemberSchema = z.object({
  test_case_id: z.string().uuid(),
  sequence: z.number().int().min(0),
});

export const GlossaryCreateSchema = z.object({
  term_de: z.string().min(1),
  term_en: z.string().min(1),
  source: z.enum(GLOSSARY_SOURCE).default('kundenspezifisch'),
  notes: z.string().nullable().default(null),
});
export const GlossaryUpdateSchema = GlossaryCreateSchema.partial();

// #47: Datenart-Stammdaten. Labels zweisprachig, aber mindestens eine Sprache gefuellt (egal welche).
const atLeastOneLabel = (v: {
  label_de?: string | null | undefined;
  label_en?: string | null | undefined;
}) => Boolean(v.label_de?.trim()) || Boolean(v.label_en?.trim());

const labelRequiredIssue = { message: 'label_required', path: ['label_de'] };

export const DataKindCreateSchema = z
  .object({
    label_de: z.string().nullable().default(null),
    label_en: z.string().nullable().default(null),
    sort_order: z.number().int().default(0),
  })
  .refine(atLeastOneLabel, labelRequiredIssue);

export const DataKindUpdateSchema = z
  .object({
    label_de: z.string().nullable().optional(),
    label_en: z.string().nullable().optional(),
    sort_order: z.number().int().optional(),
  })
  .refine(
    (v) => (v.label_de === undefined && v.label_en === undefined ? true : atLeastOneLabel(v)),
    labelRequiredIssue,
  );

export const DataKindMergeSchema = z.object({ target_id: z.string().uuid() });

export type DataKindInput = z.infer<typeof DataKindCreateSchema>;

export const TestDataCreateSchema = z.object({
  data_kind_id: z.string().uuid(),
  value: z.string().min(1),
  description: z.string().nullable().default(null),
});
export const TestDataUpdateSchema = TestDataCreateSchema.partial();

// #111: „Fehlende Übersetzungen ergänzen" im Katalog — ein Testfall (Detail) oder viele (Massen-
// aktion, der Client ruft je Testfall einzeln). `mode` geht nur ins Audit.
export const TranslationFillRequestSchema = z.object({ mode: z.enum(['single', 'bulk']) });
export type TranslationFillRequest = z.infer<typeof TranslationFillRequestSchema>;

// #111: Spiegel der Fehlerklassen aus apps/api (llm/retry.ts `LlmErrorClass` + `incomplete_answer`
// der Pipeline) — shared darf apps/api nicht importieren. translation-fill.ts prüft beide Richtungen
// per Typ-Assertion, damit die Listen nicht still auseinanderlaufen. `not_configured` = kein
// KI-Gateway eingerichtet.
export const TRANSLATION_FILL_FAILURES = [
  'rate_limit',
  'timeout',
  'server',
  'network',
  'cost_budget',
  'time_budget',
  'structural',
  'other',
  'incomplete_answer',
  'not_configured',
] as const;
export type TranslationFillFailure = (typeof TRANSLATION_FILL_FAILURES)[number];

export interface TranslationFillResult {
  test_case_id: string;
  /** Geschriebene Felder. */
  filled: number;
  /** z. B. 'title_en', 'step 2 description_de'. */
  fields: string[];
  /** Mindestens ein geschriebenes Feld liegt unter der Konfidenzschwelle. */
  review: boolean;
  /** Zwischenzeitlich von Hand gefüllt → nicht angefasst. */
  skipped_changed: number;
  /** Übersetzungslücke (Regel wie der Katalogfilter) nach dem Schreiben. */
  remaining_gap: boolean;
  failure: TranslationFillFailure | null;
}
